import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Copy, Trash2, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { useSEO } from '../hooks/useSEO';

const WordCounter: React.FC = () => {
  const [text, setText] = useState('');
  const [stats, setStats] = useState({
    characters: 0,
    charactersNoSpaces: 0,
    words: 0,
    sentences: 0,
    paragraphs: 0,
    readingTime: 0
  });

  // SEO optimization
  useSEO({
    title: 'Word Counter - Count Words, Characters & Sentences | SnapTools',
    description: 'Free online word counter tool. Count words, characters, sentences, paragraphs and reading time. Real-time text analysis with detailed statistics.',
    keywords: 'word counter, character counter, text analysis, sentence counter, paragraph counter, reading time calculator, text statistics'
  });

  useEffect(() => {
    const calculateStats = () => {
      const characters = text.length;
      const charactersNoSpaces = text.replace(/\s/g, '').length;
      const words = text.trim() ? text.trim().split(/\s+/).length : 0;
      const sentences = text.trim() ? text.split(/[.!?]+/).filter(s => s.trim().length > 0).length : 0;
      const paragraphs = text.trim() ? text.split(/\n\s*\n/).filter(p => p.trim().length > 0).length : 0;
      const readingTime = Math.ceil(words / 200); // Average reading speed: 200 words per minute

      setStats({
        characters,
        charactersNoSpaces,
        words,
        sentences,
        paragraphs,
        readingTime
      });
    };

    calculateStats();
  }, [text]);

  const clearText = () => {
    setText('');
    toast.success('Text cleared successfully');
  };

  const copyText = () => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Text copied to clipboard');
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
            <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4 px-2">
            Word Counter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Count words, characters, sentences, and paragraphs in your text. Get real-time statistics and reading time estimates.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Input Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#8438ff]/10 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-[#8438ff]" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-[#8438ff]">Enter Your Text</h2>
                </div>
                <div className="flex space-x-1 sm:space-x-2">
                  {text && (
                    <>
                      <button
                        onClick={copyText}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                        title="Copy text"
                      >
                        <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={clearText}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Clear text"
                      >
                        <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste your text here..."
                className="w-full h-48 sm:h-64 lg:h-80 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors text-sm sm:text-base"
              />
              
              <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500">
                {text.length > 0 && (
                  <span>Live counting: {stats.words} words, {stats.characters} characters</span>
                )}
              </div>
            </motion.div>
          </div>

          {/* Statistics Panel */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8 lg:sticky lg:top-8"
            >
              <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#8438ff]/10 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-[#8438ff]" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-[#8438ff]">Text Statistics</h2>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-3 sm:p-4 rounded-lg sm:rounded-xl">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#8438ff]">{stats.words}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Words</div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 sm:p-4 rounded-lg sm:rounded-xl">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600">{stats.characters}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Characters</div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 sm:p-4 rounded-lg sm:rounded-xl">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">{stats.charactersNoSpaces}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Characters (no spaces)</div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 sm:p-4 rounded-lg sm:rounded-xl">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-yellow-600">{stats.sentences}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Sentences</div>
                </div>
                
                <div className="bg-gradient-to-r from-red-50 to-pink-50 p-3 sm:p-4 rounded-lg sm:rounded-xl">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">{stats.paragraphs}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Paragraphs</div>
                </div>
                
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-indigo-600">{stats.readingTime}</div>
                  <div className="text-sm text-gray-600">Reading Time (min)</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-12"
        />
      </div>
    </div>
  );
};

export default WordCounter;
